import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { ProgressBar } from './components/ui/ProgressBar';
import { SlideTitle } from './components/SlideTitle';
import { SlideLifecycle } from './components/SlideLifecycle';
import { SlideMarketSize } from './components/SlideMarketSize';
import { SlideProblem } from './components/SlideProblem';
import { SlideAIOpportunity } from './components/SlideAIOpportunity';
import { SlideCalculator } from './components/SlideCalculator';
import { SlideAIEconomics } from './components/SlideAIEconomics';
import { SlideNewCategory } from './components/SlideNewCategory';
import { SlideStrategy } from './components/SlideStrategy';
import { SlideWhatSnakkerDoes } from './components/SlideWhatSnakkerDoes';
import { SlideBusinessModel } from './components/SlideBusinessModel';
import { SlideCampaignExample } from './components/SlideCampaignExample';
import { SlidePlatformArchitecture } from './components/SlidePlatformArchitecture';
import { SlideMoat } from './components/SlideMoat';
import { SlideTraction } from './components/SlideTraction';
import { SlideSalesFocus } from './components/SlideSalesFocus';
import { SlideFastCalculator } from './components/SlideFastCalculator';
import { SlideFinancialProjections } from './components/SlideFinancialProjections';
import { SlideCTA } from './components/SlideCTA';
import { ExecutiveSummary } from './components/ExecutiveSummary';
export function App() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(1);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const totalSlides = 18;
  const nextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      setDirection(1);
      setCurrentSlide(prev => prev + 1);
    }
  }, [currentSlide, totalSlides]);
  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setDirection(-1);
      setCurrentSlide(prev => prev - 1);
    }
  }, [currentSlide]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') nextSlide();else if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };
  const renderSlide = () => {
    switch (currentSlide) {
      case 0:
        return <SlideTitle onContinue={nextSlide} data-id="element-0" data-mp="2xun3jq65ft50" />;
      case 1:
        return <SlideLifecycle onContinue={nextSlide} data-id="element-1" data-mp="2xssgo6bbhqya" />;
      case 2:
        return <SlideMarketSize onContinue={nextSlide} data-id="element-2" data-mp="2sv0r3iy5agx9" />;
      case 3:
        return <SlideProblem onContinue={nextSlide} data-id="element-3" data-mp="18d9d06h7bks6" />;
      case 4:
        return <SlideCalculator onContinue={nextSlide} data-id="element-4" data-mp="18by8g4fxm900" />;
      case 5:
        return <SlideAIEconomics onContinue={nextSlide} data-id="element-5" data-mp="1d62hct8rcr3q" />;
      case 6:
        return <SlidePlatformArchitecture onContinue={nextSlide} data-id="element-6" data-mp="ygmu3n8fr3ss" />;
      case 7:
        return <SlideNewCategory onContinue={nextSlide} data-id="element-7" data-mp="13e54zj60jt3f" />;
      case 8:
        return <SlideAIOpportunity onContinue={nextSlide} data-id="element-8" data-mp="13cbvzx7xgws9" />;
      case 9:
        return <SlideStrategy onContinue={nextSlide} data-id="element-9" data-mp="1r5276480bc1h" />;
      case 10:
        return <SlideBusinessModel onContinue={nextSlide} data-id="element-10" data-mp="1w2aqw98szo4u" />;
      case 11:
        return <SlideWhatSnakkerDoes onContinue={nextSlide} data-id="element-11" data-mp="1i3uj0ez7frl4" />;
      case 12:
        return <SlideCampaignExample onContinue={nextSlide} data-id="element-12" data-mp="1i5x7gukplcnq" />;
      case 13:
        return <SlideMoat onContinue={nextSlide} data-id="element-13" data-mp="1m3cuyl91ssbt" />;
      case 14:
        return <SlideSalesFocus onContinue={nextSlide} data-id="element-14" data-mp="6ncnqi7cfv02" />;
      case 15:
        return <SlideFastCalculator onContinue={nextSlide} data-id="element-15" data-mp="apr7r5mr10sx" />;
      case 16:
        return <SlideFinancialProjections onContinue={nextSlide} data-id="element-16" data-mp="ano6fpg5bl8w" />;
      case 17:
        return <SlideCTA onContinue={nextSlide} data-id="element-17" data-mp="qygjbw07js0j" />;
      default:
        return null;
    }
  };
  return <div className="w-full h-screen bg-navy-800 overflow-hidden relative font-sans text-slate-900" data-id="element-18" data-mp="m4ue2owdiqer">
      <ProgressBar current={currentSlide} total={totalSlides} onBack={prevSlide} onNext={nextSlide} data-id="element-19" data-mp="h7lx2rmyosf3" />

      <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-20" data-mp="19vrjditnyph1">
        <motion.div key={currentSlide} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
        x: {
          type: 'spring',
          stiffness: 300,
          damping: 30
        },
        opacity: {
          duration: 0.2
        }
      }} className="absolute inset-0 w-full h-full" data-id="element-21" data-mp="19tx5upkq1zmz">
          {renderSlide()}
        </motion.div>
      </AnimatePresence>

      <button onClick={() => setSummaryOpen(true)} className="fixed bottom-6 left-6 z-40 flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#6C2BD9] text-sm font-semibold shadow-lg shadow-[#6C2BD9]/30 hover:bg-[#5A23B8] hover:shadow-xl hover:shadow-[#6C2BD9]/40 hover:-translate-y-0.5 transition-all" aria-label="Open executive summary" style={{
      color: '#ffffff'
    }} data-id="element-22" data-mp="1w581h02dkstl">
        <Sparkles className="w-4 h-4" style={{
        color: '#ffffff'
      }} data-id="element-23" data-mp="2euenox2do2k5" />
        <span style={{
        color: '#ffffff'
      }} data-id="element-24" data-mp="2jw41gkgziubx">
          Executive Summary
        </span>
      </button>

      <ExecutiveSummary open={summaryOpen} onClose={() => setSummaryOpen(false)} data-id="element-25" data-mp="2sv7jdl4f13ou" />
    </div>;
}